import { Component, computed, input } from '@angular/core';
import { TranslocoPipe } from '@jsverse/transloco';
import { AgentChart, AgentChartCard, AgentChartLevel, AgentChartSeries } from 'app/shared/models/agent.model';
import { HighchartsInsightModule } from 'app/shared/highcharts/highcharts.module';
import { FormaIconButton } from 'forma';
import Highcharts from 'highcharts';
import HC_more from 'highcharts/highcharts-more';
import HC_heatmap from 'highcharts/modules/heatmap';
import HC_exporting from 'highcharts/modules/exporting';
import HC_offlineExporting from 'highcharts/modules/offline-exporting';

HC_more(Highcharts);
HC_heatmap(Highcharts);
HC_exporting(Highcharts);
HC_offlineExporting(Highcharts);
// Loading the exporting module gives every chart in the app a menu button; charts that want a download add their own.
Highcharts.setOptions({ exporting: { enabled: false } });

// The chat's chart template (Figma "Eva – grafkort"). Series in trends in a fixed order; blue and orange last,
// since the navigator uses them.
const SERIES_COLORS = ['#1b8a86', '#642d91', '#6f96e6', '#d9752a', '#d9454d'];
// Bars and slices by result level, like the report pages.
const LEVEL_COLORS: Record<AgentChartLevel, string> = {
  very_low: '#e46b74',
  low: '#f6a36c',
  medium: '#fddd91',
  high: '#8fd8bc',
  very_high: '#3faf87',
  other: '#6f96e6',
};
const GREY_2 = '#4f4f4f';
const GREY_3 = '#828282';
const ALMOST_BLACK = '#141414';
const GRID = '#e8e8e8';
const AXIS_LABELS: Highcharts.CSSObject = { color: GREY_2, fontSize: '12px' };
// Row names: horizontal bars and heatmap rows.
const ROW_LABELS: Highcharts.CSSObject = { color: ALMOST_BLACK, fontSize: '14px' };
const BRAND = 'Beat by Brilliant';

type ChartWithPlot = Highcharts.Chart & { plotBackground?: Highcharts.SVGElement; plotBorder?: Highcharts.SVGElement };

/**
 * A chart an agent showed (ShowChart), drawn as a card: the kicker, the conclusion as its heading,
 * the chart and a footer with the source. The chat's template is the default look; the Highcharts
 * options the agent styled it with are laid over it. Missing values are gaps. The plot is hidden
 * from screen readers, which get the same values as a table. The download is drawn in the browser;
 * nothing is sent to Highcharts' export server.
 */
@Component({
  selector: 'app-agent-chart',
  imports: [FormaIconButton, HighchartsInsightModule, TranslocoPipe],
  host: { class: 'agent-chart' },
  template: `
    <figure class="card" [style]="cardStyle()">
      <div class="header">
        <div class="heading">
          @if (chart().kicker) {
            <p class="kicker">{{ chart().kicker }}</p>
          }
          @if (chart().title) {
            <figcaption class="conclusion" [style]="titleStyle()">{{ chart().title }}</figcaption>
          }
        </div>
        @if (card().download !== false) {
          <button
            type="button"
            formaIconButton="ghost-small"
            icon="download-simple"
            class="download"
            [attr.aria-label]="'INSIGHT.COACH.DOWNLOAD_CHART' | transloco"
            [title]="'INSIGHT.COACH.DOWNLOAD_CHART' | transloco"
            (click)="download()"></button>
        }
      </div>
      <highcharts-chart
        class="plot"
        aria-hidden="true"
        [Highcharts]="Highcharts"
        [options]="options()"
        (chartInstance)="instance = $event" />
      @if (card().footer !== false) {
        <footer class="footer">
          <span>{{ chart().source }}</span>
          <span>{{ brand }}</span>
        </footer>
      }
      <!-- A table can't be shrunk to 1px; hidden itself, its rows would still lengthen the chat's scroll. -->
      <div class="cdk-visually-hidden">
        <table>
          <caption>{{ chart().title ?? chart().kicker }}</caption>
          <thead>
            <tr>
              <td></td>
              @for (series of chart().series; track $index) {
                <th scope="col">{{ series.name }}</th>
              }
            </tr>
          </thead>
          <tbody>
            @for (category of chart().categories; track $index; let i = $index) {
              <tr>
                <th scope="row">{{ category }}</th>
                @for (series of chart().series; track $index) {
                  <td>{{ cell(series, i) }}</td>
                }
              </tr>
            }
          </tbody>
        </table>
      </div>
    </figure>
  `,
  styles: [
    `
      @use 'tokens' as t;

      :host {
        display: block;
        container-type: inline-size;
      }

      // 80 % of the chat, 280–640 px, left-aligned like the text; the whole width in a narrow chat.
      .card {
        width: clamp(280px, 80%, 640px);
        max-width: 100%;
        box-sizing: border-box;
        margin: 0;
        padding: 20px;
        border: 1px solid var(--forma-grey-5, #e0e0e0);
        border-radius: 16px;
        background: var(--forma-white, #fff);
      }

      @container (max-width: 350px) {
        .card {
          width: 100%;
        }
      }

      .header {
        display: flex;
        align-items: flex-start;
        gap: 8px;
        margin-bottom: 12px;
      }

      .heading {
        flex: 1;
        min-width: 0;
      }

      .kicker {
        @include t.label-skinny;
        margin: 0 0 4px;
      }

      .conclusion {
        @include t.heading-4;
        color: var(--forma-almost-black, #141414);
      }

      .plot {
        display: block;
        width: 100%;
      }

      .footer {
        display: flex;
        flex-wrap: wrap;
        justify-content: space-between;
        gap: 4px 16px;
        margin-top: 12px;
        padding-top: 12px;
        border-top: 1px solid var(--forma-grey-6, #f2f2f2);
        font-size: 12px;
        line-height: 16px;
        color: var(--forma-grey-2, #4f4f4f);
      }
    `,
  ],
})
export class AgentChartView {
  readonly chart = input.required<AgentChart>();

  protected readonly Highcharts = Highcharts;
  protected readonly brand = BRAND;
  protected instance: Highcharts.Chart | null = null;

  /** What the agent asked of the card itself: its look, and whether to show the footer and the download. */
  protected readonly card = computed(() => ((this.chart().options as { card?: AgentChartCard } | null | undefined)?.card ?? {}));

  protected readonly cardStyle = computed(() => {
    const card = this.card();
    const px = (value: unknown) => (typeof value === 'number' ? `${value}px` : typeof value === 'string' ? value : undefined);
    return {
      'background-color': card.backgroundColor,
      'border-color': card.borderColor,
      'border-width': px(card.borderWidth),
      'border-radius': px(card.borderRadius),
      padding: px(card.padding),
      // "full" or a number of px; never wider than the chat.
      width: card.width === 'full' ? '100%' : typeof card.width === 'number' ? `${card.width}px` : undefined,
    };
  });

  /** The heading is the card's, but the look the agent gave the title still applies to it. */
  protected readonly titleStyle = computed(() => {
    const title = (this.chart().options as Highcharts.Options | null | undefined)?.title;
    const style = (title?.style ?? {}) as Highcharts.CSSObject;
    return {
      color: style.color,
      'font-size': style.fontSize,
      'font-weight': style.fontWeight,
      'text-align': title?.align,
    };
  });

  protected readonly options = computed<Highcharts.Options>(() => {
    const chart = this.chart();
    const pie = chart.type === 'pie';
    const radar = chart.type === 'radar';
    const scatter = chart.type === 'scatter';
    const heatmap = chart.type === 'heatmap';
    const bars = chart.type === 'bar' || chart.type === 'column';
    const type = radar ? 'line' : chart.type;
    const {
      yAxis: agentYAxis,
      title: agentTitle,
      series: agentSeries,
      card: _card,
      ...agentOptions
    } = (chart.options ?? {}) as Highcharts.Options & { card?: unknown };
    const agentAxes = Array.isArray(agentYAxis) ? agentYAxis : agentYAxis ? [agentYAxis] : [];
    const axisCount = Math.max(1, agentAxes.length, chart.series.some((s) => s.yAxis === 1) ? 2 : 1);
    // Labels on every value, or on none, when the agent says so for all series; otherwise the template decides per type.
    const allLabels = (agentOptions.plotOptions?.series?.dataLabels as { enabled?: boolean } | undefined)?.enabled;
    const labels = (template: boolean) => allLabels ?? template;
    // Merged per axis: Highcharts replaces arrays rather than merging them.
    const yAxis = Array.from({ length: axisCount }, (_, i) =>
      Highcharts.merge<Highcharts.YAxisOptions>(
        {
          title: { text: undefined },
          opposite: i === 1,
          gridLineColor: GRID,
          labels: { style: AXIS_LABELS },
          // Bars carry their values; their length is the scale.
          ...(bars ? { gridLineWidth: 0, labels: { enabled: false } } : {}),
          ...(radar ? { gridLineInterpolation: 'polygon' } : {}),
          ...(heatmap ? { reversed: true, gridLineWidth: 0, labels: { style: ROW_LABELS } } : {}),
        },
        agentAxes[i] ?? {},
      ),
    );
    const plotBorderRadius = Number((agentOptions.chart as { plotBorderRadius?: unknown } | undefined)?.plotBorderRadius) || 0;

    const defaults: Highcharts.Options = {
      colors: SERIES_COLORS,
      chart: {
        type,
        polar: radar,
        height: this.height(chart),
        backgroundColor: 'transparent',
        spacing: [8, 0, 0, 0],
        style: { fontFamily: 'inherit', fontSize: '12px' },
      },
      xAxis: {
        lineColor: GRID,
        tickLength: 0,
        labels: { style: chart.type === 'bar' ? ROW_LABELS : AXIS_LABELS },
        ...(chart.type === 'bar' || heatmap ? { lineWidth: 0 } : {}),
        ...(radar ? { tickmarkPlacement: 'on', lineWidth: 0 } : {}),
        ...(scatter ? { gridLineWidth: 1, gridLineColor: GRID, title: { text: undefined } } : {}),
      },
      colorAxis: heatmap
        ? {
            min: 0,
            max: 100,
            stops: [
              [0, LEVEL_COLORS.very_low],
              [0.5, LEVEL_COLORS.medium],
              [1, LEVEL_COLORS.very_high],
            ],
          }
        : undefined,
      legend: {
        enabled: pie || (!heatmap && chart.series.length > 1),
        align: pie ? 'center' : 'left',
        verticalAlign: pie ? 'bottom' : 'top',
        margin: 12,
        padding: 0,
        symbolRadius: 6,
        symbolHeight: 8,
        symbolWidth: 8,
        itemStyle: { color: GREY_2, fontSize: '12px', fontWeight: 'normal' },
      },
      tooltip: {
        shared: !pie && !scatter && !heatmap,
        style: { fontSize: '12px' },
        ...(scatter ? { pointFormat: '{point.name}: {point.x}, {point.y}' } : {}),
        ...(heatmap ? { pointFormat: '{point.value}' } : {}),
      },
      // The app's global theme labels every point and sizes pies and column labels for report pages; the template sets its own.
      plotOptions: {
        series: {
          animation: false,
          connectNulls: true,
          dataLabels: {
            enabled: false,
            style: { color: ALMOST_BLACK, fontSize: '14px', fontWeight: 'bold', textOutline: 'none' },
          },
        },
        line: { lineWidth: 2, marker: { enabled: false } },
        spline: { lineWidth: 2, marker: { enabled: false } },
        column: {
          borderRadius: 4,
          borderWidth: 0,
          maxPointWidth: 32,
          groupPadding: 0.2,
          pointPadding: 0.1,
          dataLabels: { enabled: labels(chart.categories.length <= 12), y: -4, style: { fontSize: '14px' } },
        },
        bar: {
          borderRadius: 4,
          borderWidth: 0,
          pointWidth: 16,
          dataLabels: { enabled: labels(true), verticalAlign: 'middle', x: 4 },
        },
        pie: {
          size: '75%',
          innerSize: '65%',
          borderWidth: 2,
          allowPointSelect: false,
          showInLegend: true,
          dataLabels: {
            enabled: labels(true),
            format: '{point.name}: {point.percentage:.0f} %',
            distance: 16,
            style: { color: GREY_2, fontSize: '12px', fontWeight: 'normal' },
          },
        },
        scatter: {
          marker: { radius: 6, symbol: 'circle' },
          dataLabels: {
            enabled: labels(chart.categories.length <= 12),
            format: '{point.name}',
            style: { color: GREY_2, fontSize: '12px', fontWeight: 'normal' },
          },
        },
        heatmap: {
          borderWidth: 2,
          borderColor: '#ffffff',
          nullColor: '#f2f2f2',
          dataLabels: { enabled: labels(true), format: '{point.value}' },
        },
      },
    };
    const styling = Array.isArray(agentSeries) ? (agentSeries as Highcharts.SeriesOptionsType[]) : [];
    // Ours last: the agent styles the chart but never replaces its data, title, download or accessibility.
    return Highcharts.merge(defaults, agentOptions, {
      chart: {
        events: {
          // Highcharts has no rounded plot area; plotBorderRadius is ours.
          render() {
            const plot = this as ChartWithPlot;
            plot.plotBackground?.attr({ rx: plotBorderRadius, ry: plotBorderRadius });
            plot.plotBorder?.attr({ rx: plotBorderRadius, ry: plotBorderRadius });
          },
        },
      },
      // The card shows the title; the downloaded image gets it back.
      title: { text: undefined },
      credits: { enabled: false },
      accessibility: { enabled: false },
      exporting: {
        enabled: false,
        fallbackToExportServer: false,
        chartOptions: {
          chart: { backgroundColor: '#ffffff', spacing: [16, 16, 16, 16] },
          title: { ...agentTitle, text: chart.title ?? undefined, align: agentTitle?.align ?? 'left' },
          subtitle: { text: chart.kicker ?? undefined, align: 'left' },
          caption: { text: [chart.source, BRAND].filter(Boolean).join(' · ') },
        },
      },
      xAxis: scatter ? {} : { categories: chart.categories },
      yAxis:
        axisCount > 1
          ? yAxis
          : heatmap
            ? Highcharts.merge(yAxis[0], { categories: chart.series.map((s) => s.name) })
            : yAxis[0],
      series: heatmap
        ? [this.heatmap(chart, styling[0])]
        : chart.series.map((series, i) => this.series(chart, series, type, axisCount, styling[i], allLabels)),
    });
  });

  /**
   * The template's look for a series, the agent's styling of it, then its values. Lines get their last value at
   * their end; bars, slices and points take their level's colour; a benchmark is a grey dashed line.
   */
  private series(
    chart: AgentChart,
    series: AgentChartSeries,
    type: string,
    axisCount: number,
    styling: Highcharts.SeriesOptionsType | undefined,
    allLabels: boolean | undefined,
  ): Highcharts.SeriesOptionsType {
    const ownLabels = (styling as { dataLabels?: { enabled?: boolean } } | undefined)?.dataLabels?.enabled;
    const line = !series.benchmark && chart.type !== 'radar' && ['line', 'spline', 'area'].includes(series.type ?? type);
    const endLabel = line && (ownLabels ?? allLabels) === undefined;
    const last = endLabel ? series.data.reduce<number>((found, value, i) => (value === null ? found : i), -1) : -1;
    const data = series.data.map((y, i) => {
      const level = series.levels?.[i];
      const point: Highcharts.PointOptionsObject = { y };
      if (chart.type === 'pie' || chart.type === 'scatter') point.name = chart.categories[i];
      if (chart.type === 'scatter') point.x = series.x?.[i] ?? null;
      if (level && !line && !series.benchmark) point.color = LEVEL_COLORS[level];
      if (i === last) point.dataLabels = { enabled: true, align: 'left', x: 4, verticalAlign: 'middle', crop: false, overflow: 'allow' };
      return point;
    });
    const template = {
      ...(series.benchmark
        ? { color: GREY_3, dashStyle: 'Dash', lineWidth: 1.5, marker: { enabled: false }, dataLabels: { enabled: false } }
        : {}),
      ...(series.color ? { color: series.color } : {}),
    };
    return Highcharts.merge(template, styling ?? {}, {
      type: series.benchmark ? 'line' : (series.type ?? type),
      name: series.name,
      ...(axisCount > 1 ? { yAxis: series.yAxis ?? 0 } : {}),
      data,
    }) as Highcharts.SeriesOptionsType;
  }

  /** A heatmap is one series: a row per series, a column per category, coloured by value or by level. */
  private heatmap(chart: AgentChart, styling: Highcharts.SeriesOptionsType | undefined): Highcharts.SeriesOptionsType {
    const data = chart.series.flatMap((series, row) =>
      series.data.map((value, column) => {
        const level = series.levels?.[column];
        return { x: column, y: row, value, ...(level ? { color: LEVEL_COLORS[level] } : {}) };
      }),
    );
    return Highcharts.merge(styling ?? {}, {
      type: 'heatmap',
      name: chart.kicker ?? chart.title ?? '',
      data,
    }) as Highcharts.SeriesOptionsType;
  }

  /** The plot area is 200 px for a trend; horizontal bars and heatmap rows get a row each. */
  private height(chart: AgentChart): number {
    if (chart.type === 'bar') return Math.max(120, chart.categories.length * chart.series.length * 28 + 24);
    if (chart.type === 'heatmap') return chart.series.length * 40 + 48;
    if (chart.type === 'pie') return 280;
    if (chart.type === 'radar' || chart.type === 'scatter') return 300;
    return 240 + (chart.series.length > 1 ? 32 : 0);
  }

  /** A table cell: the value, or for a scatter point its x and y. */
  protected cell(series: AgentChartSeries, i: number): string {
    return this.chart().type === 'scatter'
      ? `${this.format(series.x?.[i])}, ${this.format(series.data[i])}`
      : this.format(series.data[i]);
  }

  protected format(value: number | null | undefined): string {
    return value === null || value === undefined ? '–' : String(value);
  }

  protected download(): void {
    const filename = (this.chart().title ?? this.chart().kicker ?? '').replace(/[^\p{L}\p{N}]+/gu, '-').replace(/^-|-$/g, '') || 'chart';
    this.instance?.exportChartLocal({ type: 'image/png', filename });
  }
}
